/**
 * Date range presets and utilities for dashboard filtering.
 * Timezone: local (browser) - Asia/Ho_Chi_Minh when in Vietnam.
 * Week: Monday = start, Sunday = end (vi-VN locale).
 */

import type { Dayjs } from 'dayjs';
import { vnDayjs } from './vnDateTime';

export type PresetId =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'last90'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'q1'
  | 'q2'
  | 'q3'
  | 'q4'
  | 'custom';

export const DEFAULT_PRESET: PresetId = 'last30';

export interface DateRangeValue {
  from: Date;
  to: Date;
  presetId: PresetId;
  label: string;
}

export interface DateRangeQuery {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

/** Get Monday 00:00 of the week containing d (vi-VN: week starts Monday) */
function startOfWeek(d: Dayjs): Dayjs {
  const dayOfWeek = d.day(); // 0=Sun, 1=Mon, ..., 6=Sat
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  return d.subtract(daysSinceMonday, 'day').startOf('day');
}

/** Get Sunday 23:59:59.999 of the week containing d */
function endOfWeek(d: Dayjs): Dayjs {
  const start = startOfWeek(d);
  return start.add(6, 'day').endOf('day');
}

/** Get quarter range (1-4) for given year. Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec */
function getQuarterRange(
  year: number,
  quarter: 1 | 2 | 3 | 4
): { start: Dayjs; end: Dayjs } {
  const monthStart = (quarter - 1) * 3;
  const start = vnDayjs().year(year).month(monthStart).date(1).startOf('day');
  const end = start.add(3, 'month').subtract(1, 'day').endOf('day');
  return { start, end };
}

/**
 * Compute date range for a preset.
 * All times in local timezone (browser).
 */
export function getPresetRange(presetId: PresetId): DateRangeValue {
  const now = vnDayjs();
  const todayStart = now.clone().startOf('day');
  const todayEnd = now.clone().endOf('day');

  let from: Dayjs;
  let to: Dayjs;
  let label: string;
  const currentYear = now.year();

  switch (presetId) {
    case 'today':
      from = todayStart;
      to = todayEnd;
      label = 'Hôm nay';
      break;
    case 'yesterday':
      from = now.subtract(1, 'day').startOf('day');
      to = now.subtract(1, 'day').endOf('day');
      label = 'Hôm qua';
      break;
    case 'last7':
      // 7 ngày qua = hôm nay + 6 ngày trước (inclusive)
      from = now.subtract(6, 'day').startOf('day');
      to = todayEnd;
      label = '7 ngày qua';
      break;
    case 'last30':
      // 30 ngày qua = hôm nay + 29 ngày trước (inclusive)
      from = now.subtract(29, 'day').startOf('day');
      to = todayEnd;
      label = '30 ngày qua';
      break;
    case 'last90':
      from = now.subtract(89, 'day').startOf('day');
      to = todayEnd;
      label = '90 ngày qua';
      break;
    case 'thisWeek': {
      const weekStart = startOfWeek(now);
      const weekEnd = endOfWeek(now);
      from = weekStart;
      to = weekEnd;
      label = 'Tuần này';
      break;
    }
    case 'lastWeek': {
      const lastWeekRef = now.subtract(1, 'week');
      from = startOfWeek(lastWeekRef);
      to = endOfWeek(lastWeekRef);
      label = 'Tuần trước';
      break;
    }
    case 'thisMonth':
      from = now.startOf('month');
      to = now.endOf('month');
      label = 'Tháng này';
      break;
    case 'lastMonth': {
      const lastMonth = now.subtract(1, 'month');
      from = lastMonth.startOf('month');
      to = lastMonth.endOf('month');
      label = 'Tháng trước';
      break;
    }
    case 'q1':
    case 'q2':
    case 'q3':
    case 'q4': {
      const q = parseInt(presetId.slice(1), 10) as 1 | 2 | 3 | 4;
      const { start, end } = getQuarterRange(currentYear, q);
      from = start;
      to = end;
      label = `Quý ${q} / năm ${currentYear}`;
      break;
    }
    case 'custom':
    default:
      // Fallback: 30 ngày qua
      from = now.subtract(29, 'day').startOf('day');
      to = todayEnd;
      label = '30 ngày qua';
  }

  return {
    from: from.toDate(),
    to: to.toDate(),
    presetId,
    label,
  };
}

/**
 * Convert Date range to query params (YYYY-MM-DD).
 * Normalizes to [startOfDay(from), endOfDay(to)].
 */
export function toQueryRange(from: Date, to: Date): DateRangeQuery {
  const fromNorm = vnDayjs(from).startOf('day');
  const toNorm = vnDayjs(to).endOf('day');
  return {
    startDate: fromNorm.format('YYYY-MM-DD'),
    endDate: toNorm.format('YYYY-MM-DD'),
  };
}

/**
 * Format range for display: "01/02/2026 – 17/02/2026"
 */
export function formatRangePreview(from: Date, to: Date): string {
  return `${vnDayjs(from).format('DD/MM/YYYY')} – ${vnDayjs(to).format(
    'DD/MM/YYYY'
  )}`;
}

/** All preset definitions for UI */
export const PRESET_OPTIONS: { id: PresetId; label: string }[] = [
  { id: 'today', label: 'Hôm nay' },
  { id: 'yesterday', label: 'Hôm qua' },
  { id: 'last7', label: '7 ngày qua' },
  { id: 'last30', label: '30 ngày qua' },
  { id: 'last90', label: '90 ngày qua' },
  { id: 'thisWeek', label: 'Tuần này' },
  { id: 'lastWeek', label: 'Tuần trước' },
  { id: 'thisMonth', label: 'Tháng này' },
  { id: 'lastMonth', label: 'Tháng trước' },
  { id: 'q1', label: `Quý 1 / năm ${vnDayjs().year()}` },
  { id: 'q2', label: `Quý 2 / năm ${vnDayjs().year()}` },
  { id: 'q3', label: `Quý 3 / năm ${vnDayjs().year()}` },
  { id: 'q4', label: `Quý 4 / năm ${vnDayjs().year()}` },
];
